import type { ComponentType } from 'react'
import { emit } from './events'

/**
 * Props are untyped at the registry boundary: they arrive as `data-*` strings
 * from markup an engineer never sees. Each component is responsible for
 * validating its own inputs.
 */
export type AnyProps = Record<string, unknown>

const components = new Map<string, ComponentType<AnyProps>>()

/**
 * Make a component mountable by name.
 *
 * Registration is allowed at any time, including after the initial page scan —
 * emitting `registry:changed` lets the embed layer re-scan for elements that
 * were skipped because the component did not exist yet.
 */
export function register<P extends AnyProps>(name: string, component: ComponentType<P>): void {
  if (components.has(name)) {
    console.warn(`[codeverse] component "${name}" is already registered; overwriting`)
  }
  components.set(name, component as ComponentType<AnyProps>)
  emit('registry:changed', { name })
}

/** Register several components at once. */
export function registerAll(entries: Record<string, ComponentType<never>>): void {
  for (const [name, component] of Object.entries(entries)) {
    register(name, component as ComponentType<AnyProps>)
  }
}

export function resolve(name: string): ComponentType<AnyProps> | undefined {
  return components.get(name)
}

export function registeredNames(): string[] {
  return [...components.keys()].sort()
}

/** Test helper — empties the registry. */
export function resetRegistry(): void {
  components.clear()
}
